import React from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem } from 'lucide-react'
import { formatPrice } from '../../../../utils/formatters'
import { PUMPKIN_ICON, WITCH_PORTRAIT_STYLE, splitSeconds } from '../../utils/halloween'
import '../../halloween-event.css'

// Стандартная карточка из дизайн-системы (top-light блик + многослойная тень)
export const CARD_CLASS =
  'rounded-2xl border border-white/10 bg-surface ' +
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]'

// Поэтапное появление секций страницы (как на главной)
export const SECTIONS_VARIANT = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } }
}

export const SECTION_VARIANT = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
}

/** Иконка тыквы; dim — ещё не собранная (серая), glow — мерцающая свеча внутри */
export const PumpkinIcon = ({ size = 20, dim = false, glow = false, className = '' }) => (
  <img
    src={PUMPKIN_ICON}
    alt=""
    width={size}
    height={size}
    draggable={false}
    className={`inline-block shrink-0 select-none ${dim ? 'opacity-25 grayscale' : ''} ${glow && !dim ? 'hev-pumpkin-glow' : ''} ${className}`}
    style={{ width: size, height: size }}
  />
)

/** Сумма в кристаллах с иконкой (суммы призов и наград приходят в кристаллах) */
export const CrystalAmount = ({ value, className = '', iconSize = 14 }) => (
  <span className={`inline-flex items-center gap-1 tabular-nums ${className}`}>
    <Gem size={iconSize} className="shrink-0" />
    {formatPrice(value)}
  </span>
)

const PROGRESS_TONES = {
  pumpkin: 'from-amber-300 via-orange-500 to-orange-600 shadow-[0_0_12px_rgba(255,120,20,0.45)]',
  toxic: 'from-lime-300 via-lime-400 to-emerald-400 shadow-[0_0_12px_rgba(124,255,79,0.45)]',
  witch: 'from-fuchsia-500 via-violet-500 to-lime-300 shadow-[0_0_12px_rgba(176,38,255,0.45)]'
}

/**
 * Полоска прогресса. По правилам эвента рисуется только у недостигнутого:
 * pct приходит целым 0–99, при достижении цели вместо полоски показываем «готово».
 */
export const ProgressBar = ({ pct, tone = 'pumpkin', className = '' }) => {
  const value = Math.min(99, Math.max(0, Number(pct) || 0))
  return (
    <div className={`relative h-2.5 overflow-hidden rounded-full bg-white/[0.07] shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)] ${className}`}>
      <motion.div
        className={`relative h-full rounded-full bg-gradient-to-r ${PROGRESS_TONES[tone]}`}
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(value, 3)}%` }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
      >
        <span className="hev-shine absolute inset-0 rounded-full" aria-hidden="true" />
      </motion.div>
    </div>
  )
}

/** Заголовок секции: иконка + текст, справа необязательный дополнительный элемент */
export const SectionTitle = ({ icon, children, extra = null }) => (
  <div className="flex items-center justify-between gap-3">
    <h2 className="flex min-w-0 items-center gap-2 text-base font-black uppercase not-italic tracking-wide text-white">
      {icon}
      <span className="truncate">{children}</span>
    </h2>
    {extra}
  </div>
)

// Ядовитая слизь на верхней кромке блока
const DRIPS = [
  { x: '7%', w: '9px', l: '18px', d: '3.4s', delay: '0s' },
  { x: '24%', w: '6px', l: '12px', d: '2.8s', delay: '-1s' },
  { x: '46%', w: '11px', l: '24px', d: '4.2s', delay: '-2s' },
  { x: '68%', w: '7px', l: '15px', d: '3s', delay: '-0.5s' },
  { x: '88%', w: '10px', l: '20px', d: '3.8s', delay: '-1.6s' }
]

export const SlimeEdge = () => (
  <div className="pointer-events-none absolute inset-x-0 top-0 z-10" aria-hidden="true">
    <div className="hev-slime" />
    {DRIPS.map((drip) => (
      <span
        key={drip.x}
        className="hev-drip"
        style={{ '--hev-x': drip.x, '--hev-w': drip.w, '--hev-l': drip.l, '--hev-d': drip.d, '--hev-delay': drip.delay }}
      />
    ))}
  </div>
)

/** Круглый портрет ведьмы из постера с вращающимся ореолом */
export const WitchPortrait = ({ size = 72, fed = false }) => (
  <div className="relative shrink-0" style={{ width: size, height: size }}>
    <div className={`hev-witch-ring absolute -inset-[3px] rounded-full ${fed ? 'opacity-100' : 'opacity-70'}`} aria-hidden="true" />
    <div
      className="absolute inset-0 rounded-full border-2 border-[#0e0b14] bg-[#1a1024] bg-no-repeat"
      style={WITCH_PORTRAIT_STYLE}
      role="img"
      aria-hidden="true"
    />
  </div>
)

/** Таймер плитками: дни · часы · минуты · секунды */
export const CountdownTiles = ({ seconds, tone = 'orange' }) => {
  const { t } = useTranslation()
  const parts = splitSeconds(seconds)
  const toneClass = tone === 'violet'
    ? 'border-violet-400/25 text-violet-100'
    : 'border-orange-400/25 text-orange-100'
  const units = [
    ['d', parts.d],
    ['h', parts.h],
    ['m', parts.m],
    ['s', parts.s]
  ]

  return (
    <div className="flex items-center gap-1.5" dir="ltr">
      {units.map(([unit, value]) => (
        <div
          key={unit}
          className={`flex min-w-[52px] flex-col items-center rounded-xl border bg-black/40 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] ${toneClass}`}
        >
          <span className="text-xl font-black leading-none tabular-nums">{String(value).padStart(2, '0')}</span>
          <span className="mt-1 text-[10px] font-semibold uppercase text-text-secondary">{t(`timeShort.${unit}`)}</span>
        </div>
      ))}
    </div>
  )
}
